import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  Notifications,
  BarChart,
  Logout,
  Group,
  Link as LinkIcon,
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../styles/theme';
import { getUserInitials } from '../utils/userInfo';
import { authService } from '../services/authService';
import ShareYourThoughts from '../components/ShareYourThoughts';
import AiRockstar from '../components/AiRockstar';
import SkillsExplorer from './SkillsExplorer';
import Colleagues from '../components/Colleagues';


const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: still clear local storage and navigate
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('user');
      localStorage.removeItem('userToken');
      navigate('/');
    }
  };

  const sidebarItems = [
    { id: 'analytics', text: 'Dashboard', icon: <BarChart /> },
    // { id: 'org-chart', text: 'Org Chart', icon: <Business /> },
    // { id: 'skill-explorer', text: 'Skill Explorer', icon: <Explore /> },
    { id: 'colleagues', text: 'Colleagues', icon: <Group /> },
    { id: 'useful-links', text: 'Useful Links', icon: <LinkIcon /> },
  ];

  const handleSidebarClick = (itemId) => {
    if (itemId === 'useful-links') {
      navigate('/links');
    } else {
      setActiveTab(itemId);
    }
  };

  const handleProfileClick = () => {
    setActiveTab(); // Reset to default tab when profile is clicked
    navigate('/profile');
  };

  // Get user info from localStorage (set by OAuthCallback)
  const userInitials = getUserInitials();

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', height: '100vh', backgroundColor: 'white' }}>
        {/* Left Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              backgroundColor: '#0D004A',
              border: 'none',
            },
          }}
        >
          {/* Logo */}
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#0D004A',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                  }}
                >
                  airo
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                }}
              >
                KYC
              </Typography>
            </Box>
          </Box>

          {/* Navigation Items */}
          <Box sx={{ flexGrow: 1, pt: 2 }}>
            <List>
              {sidebarItems.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ px: 2, mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleSidebarClick(item.id)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: activeTab === item.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: activeTab === item.id ? 'bold' : 'normal',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Logout */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                  <Logout />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ color: 'white' }}
                />
              </ListItemButton>
            </ListItem>
          </Box>
        </Drawer>

        {/* Main Content (No changes here) */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Top Bar */}
          <AppBar
            position="static"
            elevation={0}
            sx={{
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              <TextField
                placeholder="Search for colleagues or skills..."
                variant="outlined"
                size="small"
                sx={{
                  width: 400,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 3,
                    '& fieldset': { border: 'none' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton sx={{ color: '#666666' }}>
                  <Notifications />
                </IconButton>
                <Avatar
                  onClick={handleProfileClick}
                  sx={{ 
                    bgcolor: '#FF0000', 
                    width: 40, 
                    height: 40, 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {userInitials}
                </Avatar>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Content Area */}
          <Box sx={{ flexGrow: 1, p: 4, backgroundColor: 'white', overflowY: 'auto' }}>
            {activeTab === 'analytics' && (
              <>
                {/* Share Your Thoughts Section */}
                <ShareYourThoughts />

                {/* AiRockstar of the Month */}
                <AiRockstar />
              </>
            )}

            {activeTab === 'skill-explorer' && (
              <SkillsExplorer />
            )}

            {activeTab === 'colleagues' && (
              <Colleagues />
            )}

            {activeTab === 'org-chart' && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>
                  Org Chart
                </Typography>
                <Typography variant="body1" sx={{ color: '#999' }}>
                  Organization chart will be displayed here
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

      </Box>
    </ThemeProvider>
  );
};

export default Dashboard;
